'use client';
import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { Input } from '../Input/Input.js';
import { Body } from '../Body/Body.js';
import { IconChevronDown } from '../Icons/Directional/ChevronDown.js';
import { isMobileDevice } from '../../utils/dom.js';

const Icon = _ref => {
  let {
    active,
    inactive,
    position = 'left',
    location,
    selected
  } = _ref;
  let IconComponent;
  if (position !== location) return null;
  if (selected && active) IconComponent = active;
  if (!selected && inactive) IconComponent = inactive;
  if (!IconComponent) return null;
  return /*#__PURE__*/React__default.createElement("span", {
    className: `b-select__option-icon b-select__option-icon--${location}`,
    "data-testid": `select-icon-${location}`
  }, /*#__PURE__*/React__default.createElement(IconComponent, null));
};
const Dropdown = _ref2 => {
  let {
    filteredOptions,
    handleSelection,
    internalValue,
    highlightedIndex,
    placeholder,
    isOpen,
    optionProps,
    noOptionsText,
    icon,
    position
  } = _ref2;
  if (!isOpen) return null;
  return /*#__PURE__*/React__default.createElement("ul", {
    className: classnames('b-select__dropdown', `b-select__dropdown--${position}`),
    role: "listbox",
    "aria-label": placeholder || 'Select an option'
  }, filteredOptions?.length > 0 ? filteredOptions.map((option, index) => /*#__PURE__*/React__default.createElement("li", {
    key: option.value,
    className: "b-select__dropdown-item"
  }, /*#__PURE__*/React__default.createElement("button", {
    onClick: event => handleSelection(option, event),
    role: "option",
    "aria-selected": internalValue === option.label,
    disabled: option.disabled,
    className: `b-select__option  ${internalValue === option.label ? 'b-select__option--selected' : ''} ${index === highlightedIndex ? 'b-select__option--hover' : ''}`
  }, /*#__PURE__*/React__default.createElement(Icon, _extends({
    location: "left",
    selected: internalValue === option.label
  }, icon)), /*#__PURE__*/React__default.createElement(Body, _extends({
    type: "span",
    variant: "body-sm"
  }, optionProps), option.label), /*#__PURE__*/React__default.createElement(Icon, _extends({
    location: "right",
    selected: internalValue === option.label
  }, icon))))) : /*#__PURE__*/React__default.createElement("li", {
    className: "b-select__dropdown-item--no-options"
  }, /*#__PURE__*/React__default.createElement(Body, _extends({
    type: "span",
    variant: "body-sm"
  }, optionProps), noOptionsText)));
};
const Select = _ref3 => {
  let {
    className = '',
    options,
    optionProps,
    optionIcon,
    value = '',
    onClick,
    onChange,
    onSelect = () => null,
    arrow: Arrow,
    readOnly,
    filter = true,
    noOptionsText = 'No options found',
    dropdownPosition = 'absolute',
    ...rest
  } = _ref3;
  const [internalValue, setInternalValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef(null);
  const DropdownIcon = Arrow || IconChevronDown;
  useEffect(() => {
    function handleOutsideClick(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);
  useEffect(() => {
    if (filter && selectedOption || !filter) {
      setFilteredOptions(options);
    }
  }, [selectedOption, options, filter]);
  const handleOnChange = event => {
    const inputValue = event.target.value;
    setInternalValue(inputValue);
    if (onChange) onChange(event);
    if (filter && !selectedOption) {
      setFilteredOptions(options.filter(option => option.label.toLocaleLowerCase().includes(inputValue)));
    }
  };
  const inputRef = useRef(null);
  const handleOnClick = event => {
    event.preventDefault();
    if (document?.activeElement?.tagName === 'INPUT') {
      inputRef.current?.blur();
    } else {
      inputRef.current?.focus();
    }
    setIsOpen(!isOpen);
    if (onClick) onClick(event);
  };
  const handleSelection = (option, event) => {
    setInternalValue(option.label);
    setIsOpen(false);
    setSelectedOption(option);
    onSelect(option, event);
  };
  const handleKeyDown = event => {
    switch (event.key) {
      case 'ArrowUp':
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(prevIndex => prevIndex <= 0 ? filteredOptions.length - 1 : prevIndex - 1);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prevIndex => prevIndex >= filteredOptions.length - 1 ? 0 : prevIndex + 1);
        }
        break;
      case 'Enter':
        if (isOpen) {
          event.preventDefault();
          if (highlightedIndex !== -1) {
            handleSelection(filteredOptions[highlightedIndex], event);
          }
        }
        break;
      case 'Escape':
        if (isOpen) setIsOpen(false);
        break;
    }
  };
  const handleFocus = () => {
    setIsOpen(true);
    setSelectedOption(null);
    setFilteredOptions(options);
  };
  return /*#__PURE__*/React__default.createElement("div", {
    className: classnames(className, 'b-select', {
      'b-select--open': isOpen
    }),
    ref: containerRef,
    "data-testid": "select"
  }, /*#__PURE__*/React__default.createElement(Input, _extends({
    type: "text",
    value: internalValue
  }, rest, {
    onFocus: handleFocus,
    onChange: handleOnChange,
    onKeyDown: handleKeyDown,
    onMouseDown: handleOnClick,
    readOnly: isMobileDevice() || readOnly,
    ref: inputRef,
    after: /*#__PURE__*/React__default.createElement(Dropdown, {
      filteredOptions: filteredOptions,
      handleSelection: handleSelection,
      internalValue: internalValue,
      highlightedIndex: highlightedIndex,
      placeholder: rest?.placeholder,
      isOpen: isOpen,
      optionProps: optionProps,
      noOptionsText: noOptionsText,
      icon: optionIcon,
      position: dropdownPosition
    })
  }), /*#__PURE__*/React__default.createElement("button", {
    className: "b-select__toggle",
    onClick: () => setIsOpen(!isOpen),
    "aria-label": "Toggle Dropdown",
    "aria-expanded": isOpen
  }, /*#__PURE__*/React__default.createElement(DropdownIcon, {
    className: isOpen ? 'b-select__toggle--open' : ''
  }))));
};
Select.displayName = 'Select';

export { Select };
